import { useState, useEffect, useMemo } from 'react';
import { globalSearchService } from '../services/globalSearchService';
import { API_BASE_URL, getTenantId } from '../config/api';

export const useGlobalSearch = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [searchData, setSearchData] = useState({
    clients: [],
    prestataires: [],
    missions: [],
    services: [],
    vehicules: [],
    signalements: []
  });

  // Fonction pour charger les données de tous les modules
  const loadSearchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const tenantId = getTenantId();
      
      const headers = {
        'Content-Type': 'application/json',
        'x-tenant-id': tenantId
      };
      
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const [
        clientsRes,
        prestatairesRes,
        missionsRes,
        servicesRes,
        vehiculesRes,
        signalementsRes
      ] = await Promise.all([
        fetch(`${API_BASE_URL}/clients?limit=10000`, { headers }),
        fetch(`${API_BASE_URL}/prestataires`, { headers }),
        fetch(`${API_BASE_URL}/missions`, { headers }),
        fetch(`${API_BASE_URL}/services`, { headers }),
        fetch(`${API_BASE_URL}/vehicules`, { headers }),
        fetch(`${API_BASE_URL}/signalements`, { headers })
      ]);

      const parseRes = async (res) => {
        if (!res.ok) return [];
        const data = await res.json();
        return Array.isArray(data) ? data : (data.data || data.results || data.clients || data.missions || data.services || data.vehicules || data.signalements || data.prestataires || []);
      };

      const newSearchData = {
        clients: await parseRes(clientsRes),
        prestataires: await parseRes(prestatairesRes),
        missions: await parseRes(missionsRes),
        services: await parseRes(servicesRes),
        vehicules: await parseRes(vehiculesRes),
        signalements: await parseRes(signalementsRes)
      };
      
      setSearchData(newSearchData);
    } catch (error) {
      console.warn('Erreur lors du chargement des données de recherche:', error);
    }
  };

  // Charger les données au montage du composant
  useEffect(() => {
    loadSearchData();
  }, []);

  // Effectuer la recherche avec debounce
  useEffect(() => {
    const performSearch = async () => {
      if (!searchQuery || searchQuery.length < 2) {
        setSearchResults([]);
        setShowResults(false);
        return;
      }

      setIsSearching(true);
      try {
        const results = await globalSearchService.searchAll(searchQuery, searchData);
        const formattedResults = globalSearchService.formatResults(results);
        setSearchResults(formattedResults);
        setShowResults(true);
      } catch (error) {
        console.error('Erreur lors de la recherche:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    };

    // Debounce de 300ms
    const timeoutId = setTimeout(performSearch, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, searchData]);

  // Fonctions utilitaires
  const updateSearchQuery = (query) => {
    setSearchQuery(query);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setShowResults(false);
  };

  const hideResults = () => {
    setShowResults(false);
  };

  const groupedResults = useMemo(() => {
    return globalSearchService.groupResultsByModule(searchResults);
  }, [searchResults]);

  return {
    searchQuery,
    searchResults,
    groupedResults,
    isSearching,
    showResults,
    updateSearchQuery,
    clearSearch,
    hideResults,
    // Statistiques
    totalResults: searchResults.length,
    hasResults: searchResults.length > 0 && searchResults[0].id !== 'no-results'
  };
};